/**
 * NPEContractListPage — danh sách NPE Contracts.
 * UX/UI đồng bộ ContractListView (TableVirtuoso + popover filter + TablePagination).
 */
import { type MouseEvent, useMemo, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  InputLabel,
  MenuItem,
  Popover,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Typography,
} from '@mui/material'
import { alpha, type SxProps, type Theme } from '@mui/material/styles'
import { TableVirtuoso } from 'react-virtuoso'
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import dayjs from 'dayjs'
import { useNavigate } from 'react-router-dom'

import ConfirmDialog from '@/components/ConfirmDialog'
import RoleGuard from '@/components/RoleGuard'
import { useSnackbar } from '@/hooks/useSnackbar'
import { usePagePermission } from '../../hooks/usePagePermission'
import {
  useCopyNPEContractMutation,
  useDeleteNPEContractMutation,
  useGetNPEContractQuery,
  useLazyPrintNPEContractQuery,
  useListNPEContractsQuery,
} from '@/store/api/npeContractsApi'
import type { NPEContractOut } from '@/types/api'
import { getErrorMessage } from '@/utils/error'

import NPEContractFormDialog from './NPEContractFormDialog'

type FilterKind = 'search' | 'vendor' | 'certification' | 'date'

const ALL_ROWS_PAGE_SIZE = 1_000_000

const filterBarBtnSx: SxProps<Theme> = (theme) => ({
  minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, textTransform: 'none', fontSize: '0.7rem', lineHeight: 1.1,
  '&.MuiButton-outlined': {
    borderColor: alpha(theme.palette.text.primary, 0.14),
    '&:hover': { borderColor: alpha(theme.palette.text.primary, 0.24), backgroundColor: alpha(theme.palette.text.primary, 0.04) },
  },
})
const actionToolbarBtnSx: SxProps<Theme> = { minWidth: 0, px: 0.75, py: 0.2, minHeight: 26, fontSize: '0.72rem', lineHeight: 1.1, textTransform: 'none' }

type ColumnKind = 'text' | 'date' | 'number' | 'bool'
type ColumnDef = { key: keyof NPEContractOut; label: string; kind?: ColumnKind; align?: 'right' }

const TABLE_COLUMNS: ColumnDef[] = [
  { key: 'contract_no', label: 'NPE No' },
  { key: 'contract_date', label: 'Ngày HĐ', kind: 'date' },
  { key: 'cp_code', label: 'Mã NCC' },
  { key: 'supplier_name', label: 'Nhà cung cấp' },
  { key: 'quantity_kg', label: 'SL (kg)', kind: 'number', align: 'right' },
  { key: 'quality_code', label: 'Quality' },
  { key: 'delivery_time', label: 'Delivery', kind: 'date' },
  { key: 'received', label: 'Received', kind: 'number', align: 'right' },
  { key: 'to_be_fixed', label: 'To be fixed', kind: 'number', align: 'right' },
  { key: 'paid', label: 'Paid', kind: 'number', align: 'right' },
  { key: 'is_signed_by_gm', label: 'Signed GM', kind: 'bool' },
  { key: 'is_sent_to_client', label: 'Sent Client', kind: 'bool' },
  { key: 'is_signed_by_client', label: 'Signed Client', kind: 'bool' },
  { key: 'certification', label: 'Cert' },
  { key: 'note', label: 'Ghi chú' },
]

const ACCESS_MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']
const formatAccessDate = (v: unknown): string => { if (!v) return ''; const d = new Date(String(v)); if (Number.isNaN(d.getTime())) return String(v); return `${`${d.getDate()}`.padStart(2, '0')}-${ACCESS_MONTHS[d.getMonth()]}-${`${d.getFullYear()}`.slice(-2)}` }
const formatAccessNumber = (v: number): string => Number.isInteger(v) ? v.toLocaleString('en-US') : v.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })

const distinctValues = (rows: NPEContractOut[], key: keyof NPEContractOut): string[] => {
  const values = new Set<string>()
  for (const r of rows) { const v = r[key]; if (v == null) continue; const t = String(v).trim(); if (t) values.add(t) }
  return [...values].sort((a, b) => a.localeCompare(b))
}

const rowMatchesSearch = (row: NPEContractOut, term: string): boolean => {
  const needle = term.trim().toLowerCase()
  if (!needle) return true
  return TABLE_COLUMNS.some((col) => {
    const value = row[col.key]
    if (value == null) return false
    return String(value).toLowerCase().includes(needle)
  })
}

const cellValue = (row: NPEContractOut, col: ColumnDef): string => {
  const val = row[col.key]
  if (val == null) return ''
  if (col.kind === 'date') return formatAccessDate(val)
  if (col.kind === 'bool') return val ? 'Yes' : 'No'
  if (col.kind === 'number' && typeof val === 'number') return formatAccessNumber(val)
  if (typeof val === 'boolean') return val ? 'Yes' : 'No'
  if (typeof val === 'number') return formatAccessNumber(val)
  return String(val)
}

export function NPEContractListPage() {
  const { canEdit } = usePagePermission()
  const snack = useSnackbar()
  const navigate = useNavigate()

  const [selectedId, setSelectedId] = useState<number | null>(null)
  const [page, setPage] = useState(0)
  const [rowsPerPage, setRowsPerPage] = useState<number>(-1)

  const [searchTerm, setSearchTerm] = useState('')
  const [vendorFilter, setVendorFilter] = useState('')
  const [certFilter, setCertFilter] = useState('')
  const [dateFrom, setDateFrom] = useState<string>('')
  const [dateTo, setDateTo] = useState<string>('')

  const [filterPopover, setFilterPopover] = useState<{ kind: FilterKind; anchor: HTMLElement } | null>(null)
  const closeFilterPopover = () => setFilterPopover(null)
  const openFilterPopover = (kind: FilterKind, e: MouseEvent<HTMLElement>) => setFilterPopover({ kind, anchor: e.currentTarget })

  const hasActiveFilters = Boolean(searchTerm.trim() || vendorFilter || certFilter || dateFrom || dateTo)
  const clearAllFilters = () => { setSearchTerm(''); setVendorFilter(''); setCertFilter(''); setDateFrom(''); setDateTo(''); setPage(0) }

  const [createOpen, setCreateOpen] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [confirmDelete, setConfirmDelete] = useState<NPEContractOut | null>(null)

  const { data, isLoading, isFetching, error } = useListNPEContractsQuery({ page: 1, page_size: ALL_ROWS_PAGE_SIZE })
  const { data: editingDetail } = useGetNPEContractQuery(editingId ?? 0, { skip: !editingId })

  const [deleteCt, { isLoading: deleting }] = useDeleteNPEContractMutation()
  const [copyCt] = useCopyNPEContractMutation()
  const [triggerPrint] = useLazyPrintNPEContractQuery()

  const baseRows = useMemo(() => data?.items ?? [], [data])

  const filteredRows = useMemo(() =>
    baseRows.filter((row) => {
      if (!rowMatchesSearch(row, searchTerm)) return false
      if (vendorFilter && row.cp_code !== vendorFilter) return false
      if (certFilter && row.certification !== certFilter) return false
      if (dateFrom && (!row.contract_date || row.contract_date < dateFrom)) return false
      if (dateTo && (!row.contract_date || row.contract_date > dateTo)) return false
      return true
    }),
    [baseRows, searchTerm, vendorFilter, certFilter, dateFrom, dateTo],
  )

  const rows = useMemo(() => rowsPerPage === -1 ? filteredRows : filteredRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage), [filteredRows, page, rowsPerPage])

  const vendorOptions = useMemo(() => {
    const seen = new Map<string, string>()
    for (const r of baseRows) { if (!r.cp_code) continue; if (!seen.has(r.cp_code)) seen.set(r.cp_code, `${r.cp_code}${r.supplier_name ? ` — ${r.supplier_name}` : ''}`) }
    return [...seen.entries()].sort((a, b) => a[0].localeCompare(b[0]))
  }, [baseRows])
  const certOptions = useMemo(() => distinctValues(baseRows, 'certification'), [baseRows])

  const gridKey = `${rowsPerPage}:${page}:${filteredRows.length}`
  const selectedRow = useMemo(() => baseRows.find((r) => r.consignment_p_id === selectedId) ?? null, [baseRows, selectedId])

  const handleView = () => { if (!selectedRow) return; navigate(`/purchase/npe-contracts/${selectedRow.consignment_p_id}`) }
  const handleEdit = () => { if (!selectedRow) return; setEditingId(selectedRow.consignment_p_id) }
  const handleCopy = async () => {
    if (!selectedRow) return
    try { const res = await copyCt(selectedRow.consignment_p_id).unwrap(); snack.success(res.message ?? `Đã copy thành ${res.data?.contract_no ?? ''}`) }
    catch (err) { snack.error(getErrorMessage(err, 'Copy NPE Contract thất bại')) }
  }
  const handleConfirmDelete = async () => {
    if (!confirmDelete) return
    try { await deleteCt(confirmDelete.consignment_p_id).unwrap(); snack.success(`Đã xoá NPE Contract ${confirmDelete.contract_no}`); setConfirmDelete(null) }
    catch (err) { snack.error(getErrorMessage(err, 'Xoá thất bại')) }
  }
  const handlePrint = async () => {
    if (!selectedRow) return
    try { await triggerPrint(selectedRow.consignment_p_id).unwrap(); snack.info('Yêu cầu in đã gửi') }
    catch (err) { snack.error(getErrorMessage(err, 'In PDF thất bại')) }
  }

  return (
    <Box sx={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <Stack direction="row" flexWrap="wrap" useFlexGap justifyContent="space-between" alignItems="center" sx={{ mb: 0.5 }}>
        <Stack direction="row" spacing={0.375} flexWrap="wrap" useFlexGap alignItems="center" sx={{ minWidth: 0 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mr: 0.25 }}>Filter</Typography>
          <Button size="small" variant={searchTerm.trim() ? 'contained' : 'outlined'} color={searchTerm.trim() ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('search', e)} sx={filterBarBtnSx}>Search</Button>
          <Button size="small" variant={vendorFilter ? 'contained' : 'outlined'} color={vendorFilter ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('vendor', e)} sx={filterBarBtnSx}>Vendor</Button>
          <Button size="small" variant={certFilter ? 'contained' : 'outlined'} color={certFilter ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('certification', e)} sx={filterBarBtnSx}>Certification</Button>
          <Button size="small" variant={dateFrom || dateTo ? 'contained' : 'outlined'} color={dateFrom || dateTo ? 'primary' : 'inherit'} endIcon={<ArrowDropDownIcon sx={{ fontSize: 16 }} />} onClick={(e) => openFilterPopover('date', e)} sx={filterBarBtnSx}>Date</Button>
          {hasActiveFilters && <Button size="small" variant="text" color="secondary" onClick={clearAllFilters} sx={filterBarBtnSx}>Clear All</Button>}
        </Stack>
        <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap justifyContent="flex-end" alignItems="center">
          <Button variant="contained" size="small" color="success" sx={actionToolbarBtnSx} disabled={!canEdit} onClick={() => setCreateOpen(true)}>New</Button>
          <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow} onClick={handleView}>View</Button>
          <Button variant="outlined" size="small" color="warning" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={handleEdit}>Edit</Button>
          <Button variant="outlined" size="small" color="primary" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={() => void handleCopy()}>Copy</Button>
          <Button variant="outlined" size="small" color="error" sx={actionToolbarBtnSx} disabled={!selectedRow || !canEdit} onClick={() => selectedRow && setConfirmDelete(selectedRow)}>Delete</Button>
          <Button variant="outlined" size="small" sx={{ ...actionToolbarBtnSx, color: 'primary.main', borderColor: 'primary.main' }} disabled={!selectedRow} onClick={() => void handlePrint()}>Print</Button>
        </Stack>
      </Stack>
      <Divider sx={{ mb: 0.5 }} />

      {error ? <Alert severity="error" sx={{ mb: 1 }}>Failed to load NPE Contracts.</Alert> : null}

      <Box className="app-dataframe">
        <Box className="app-dataframe-toolbar" sx={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: 1, flexWrap: 'wrap', minHeight: 30, py: 0.125 }}>
          <Stack direction="row" alignItems="center" spacing={0.75} sx={{ flexShrink: 0 }}>
            {(isLoading || isFetching) && <CircularProgress size={15} />}
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Total: {filteredRows.length}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ mr: 0.5, fontSize: '0.68rem' }}>Loaded: {baseRows.length}</Typography>
            <TablePagination component="div" count={filteredRows.length} page={rowsPerPage === -1 ? 0 : page} onPageChange={(_, p) => setPage(p)} rowsPerPage={rowsPerPage} onRowsPerPageChange={(e) => { setRowsPerPage(Number(e.target.value)); setPage(0) }} rowsPerPageOptions={[20, 50, 100, { label: 'All', value: -1 }]} labelRowsPerPage="Rows" sx={{ flexShrink: 0, '& .MuiTablePagination-toolbar': { minHeight: 32, px: 0.5 }, '& .MuiTablePagination-selectLabel, & .MuiTablePagination-displayedRows, & .MuiTablePagination-select': { fontSize: '0.72rem' } }} />
          </Stack>
        </Box>

        <Popover open={filterPopover !== null} anchorEl={filterPopover?.anchor ?? null} onClose={closeFilterPopover} anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }} transformOrigin={{ vertical: 'top', horizontal: 'left' }} PaperProps={{ sx: { p: 1.5, minWidth: 240, maxWidth: 360 } }}>
          {filterPopover?.kind === 'search' && (
            <Stack spacing={1}>
              <TextField size="small" fullWidth autoFocus label="Tìm kiếm" placeholder="NPE no, vendor..." value={searchTerm} onChange={(e) => { setSearchTerm(e.target.value); setPage(0) }} />
              <Button size="small" variant="text" onClick={() => { setSearchTerm(''); setPage(0); closeFilterPopover() }}>Clear Search</Button>
            </Stack>
          )}
          {filterPopover?.kind === 'vendor' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Vendor</InputLabel>
              <Select label="Vendor" value={vendorFilter} onChange={(e) => { setVendorFilter(String(e.target.value)); setPage(0); closeFilterPopover() }}>
                <MenuItem value="">All / Clear</MenuItem>
                {vendorOptions.map(([code, label]) => <MenuItem key={code} value={code}>{label}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {filterPopover?.kind === 'certification' && (
            <FormControl size="small" fullWidth>
              <InputLabel>Certification</InputLabel>
              <Select label="Certification" value={certFilter} onChange={(e) => { setCertFilter(String(e.target.value)); setPage(0); closeFilterPopover() }}>
                <MenuItem value="">All / Clear</MenuItem>
                {certOptions.map((v) => <MenuItem key={v} value={v}>{v}</MenuItem>)}
              </Select>
            </FormControl>
          )}
          {filterPopover?.kind === 'date' && (
            <Stack spacing={1} sx={{ minWidth: 240 }}>
              <DatePicker label="Từ ngày" format="DD/MM/YYYY" value={dateFrom ? dayjs(dateFrom) : null} onChange={(d) => { setDateFrom(d ? d.format('YYYY-MM-DD') : ''); setPage(0) }} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
              <DatePicker label="Đến ngày" format="DD/MM/YYYY" value={dateTo ? dayjs(dateTo) : null} onChange={(d) => { setDateTo(d ? d.format('YYYY-MM-DD') : ''); setPage(0) }} slotProps={{ textField: { size: 'small', fullWidth: true } }} />
              <Button size="small" variant="text" onClick={() => { setDateFrom(''); setDateTo(''); setPage(0) }}>Clear Date</Button>
            </Stack>
          )}
        </Popover>

        <Box sx={{ flex: 1, minHeight: 320, overflow: 'hidden' }}>
          <TableVirtuoso
            key={gridKey}
            style={{ height: '100%' }}
            data={rows}
            fixedHeaderContent={() => (
              <TableRow>
                {TABLE_COLUMNS.map((col) => (
                  <TableCell key={String(col.key)} align={col.align} sx={{ fontWeight: 700, whiteSpace: 'nowrap', bgcolor: 'background.default' }}>{col.label}</TableCell>
                ))}
              </TableRow>
            )}
            itemContent={(_: number, row: NPEContractOut) =>
              TABLE_COLUMNS.map((col) => {
                const val = cellValue(row, col)
                return (
                  <TableCell key={String(col.key)} align={col.align} sx={{ whiteSpace: 'nowrap', py: 0.5 }}>
                    <Box component="span" title={val || '-'} sx={{ display: 'inline-block', width: '100%' }}>{val || '-'}</Box>
                  </TableCell>
                )
              })
            }
            components={{
              Table: (props: any) => <Table {...props} size="small" stickyHeader sx={{ minWidth: 2200, width: '100%' }} />,
              TableHead: ((props: any) => <TableHead {...props} component="thead" />) as any,
              TableBody: ((props: any) => <TableBody {...props} component="tbody" />) as any,
              TableRow: (props: any) => {
                const idx = Number((props as any)['data-item-index'])
                const id = Number.isFinite(idx) ? rows[idx]?.consignment_p_id : null
                return <TableRow {...props} hover selected={id != null && id === selectedId} sx={{ cursor: 'pointer' }} onClick={() => { if (id != null) setSelectedId(id) }} />
              },
            }}
          />
        </Box>
      </Box>

      <NPEContractFormDialog open={createOpen} mode="create" onClose={() => setCreateOpen(false)} />
      <NPEContractFormDialog open={!!editingId && !!editingDetail?.data} mode="edit" initialValue={editingDetail?.data ?? null} onClose={() => setEditingId(null)} />
      <ConfirmDialog open={!!confirmDelete} title="Xoá NPE Contract" message={confirmDelete ? `Bạn chắc chắn muốn xoá NPE Contract ${confirmDelete.contract_no}?` : ''} destructive loading={deleting} onConfirm={() => void handleConfirmDelete()} onCancel={() => setConfirmDelete(null)} />
    </Box>
  )
}

export default NPEContractListPage
